import { BottomSheet, LoadingSpinner, type UseModalState } from "@clipboard-health/ui-components";
import { isDefined, toError } from "@clipboard-health/util-ts";
import { DialogContent, Stack } from "@mui/material";
import { APP_V2_APP_EVENTS, logError } from "@src/appV2/lib/analytics";
import { useToast } from "@src/appV2/lib/Notifications/Toasts/useToast";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { useWorkerEtaImprovementsConfig } from "@src/appV2/redesign/ClipboardScore/hooks/useWorkerEtaImprovementsConfig";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import { useGetWorkerShift } from "@src/appV2/redesign/WorkerShifts/api/useGetWorkerShift";
import { SHIFT_DETAILS_FULL_PATH } from "@src/appV2/redesign/WorkerShifts/path";
import { parseISO } from "date-fns";
import { generatePath, useHistory } from "react-router-dom";

import { useGetWorkerTrips } from "../useGetWorkerTrips";
import { UpdateEtaBottomSheetForm } from "./Form";
import { type UpdateEtaBottomSheetSource } from "./types";
import { getEtaUpdateAvailableTimes } from "./utils/getEtaUpdateAvailableTimes";

interface UpdateEtaBottomSheetContainerProps {
  modalState: UseModalState;
  shiftId: string;
  source?: UpdateEtaBottomSheetSource;
}

export function UpdateEtaBottomSheetContainer(props: UpdateEtaBottomSheetContainerProps) {
  const { modalState, shiftId, source } = props;

  const now = useCurrentDate(1000);
  const { showErrorToast } = useToast();
  const history = useHistory();

  const { maxMinutesAheadToUpdateEta } = useWorkerEtaImprovementsConfig();

  const {
    data: shift,
    isLoading: isShiftLoading,
    error: shiftError,
  } = useGetWorkerShift(shiftId, {
    enabled: isDefined(shiftId),
  });

  const workplaceId = shift?.response.facilityId;
  const workplaceTimezone = shift?.response.facility.tmz ?? "America/Los_Angeles";

  const {
    data: workerTrips,
    isLoading: isWorkerTripsLoading,
    error: workerTripsError,
  } = useGetWorkerTrips(
    {
      shiftId,
    },
    {
      enabled: isDefined(workplaceId),
    }
  );

  const shiftStartTime = shift?.response.start;
  const etaInMinutes = workerTrips?.[0]?.attributes.currentEtaDuration;

  const etaUpdateAvailableTimes =
    isDefined(shift) && isDefined(etaInMinutes)
      ? getEtaUpdateAvailableTimes({
          shiftStartTime: shift.response.start,
          shiftEndTime: shift.response.end,
          targetArrivalTime: shift.response.computedArrival?.targetArrivalTime,
          updatedEta: workerTrips?.[0]?.attributes.workerDeclaredEta,
          etaInMinutes,
          now,
          maxMinutesAheadToUpdateEta,
        })
      : undefined;

  const isBottomSheetLoading =
    isShiftLoading ||
    isWorkerTripsLoading ||
    !isDefined(shiftStartTime) ||
    !isDefined(workplaceId) ||
    !isDefined(etaUpdateAvailableTimes);

  const isBottomSheetError =
    isDefined(shiftError) ||
    isDefined(workerTripsError) ||
    (isDefined(etaUpdateAvailableTimes) && etaUpdateAvailableTimes.length === 0);

  if (isBottomSheetError) {
    showErrorToast(
      "Something went wrong while updating your ETA. Please try again or contact support."
    );
    logError(APP_V2_APP_EVENTS.UPDATE_ETA_BOTTOM_SHEET_ERROR, {
      error: toError(shiftError ?? workerTripsError) ?? "No eta update available times",
      metadata: {
        shiftId,
        workplaceId,
        shiftError: toError(shiftError),
        workerTripsError: toError(workerTripsError),
        etaUpdateAvailableTimes,
      },
    });
    history.replace(generatePath(SHIFT_DETAILS_FULL_PATH, { shiftId }));
    return;
  }

  if (isBottomSheetLoading) {
    return (
      <BottomSheet
        modalState={modalState}
        header={<BottomSheetHeader onClose={modalState.closeModal} />}
      >
        <DialogContent sx={{ textAlign: "center" }}>
          <Stack direction="column" alignItems="center" justifyContent="center" px={6} py={10}>
            <LoadingSpinner size="medium" />
          </Stack>
        </DialogContent>
      </BottomSheet>
    );
  }

  return (
    <UpdateEtaBottomSheetForm
      modalState={modalState}
      shiftId={shiftId}
      workplaceId={workplaceId}
      workplaceTimezone={workplaceTimezone}
      shiftStartTime={parseISO(shiftStartTime)}
      etaUpdateAvailableTimes={etaUpdateAvailableTimes}
      source={source}
    />
  );
}
